import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Button, Stack, Divider, Typography, MenuItem, Select, FormControl, Skeleton, Paper, Grid } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import FutureContractRow from '../../components/FutureContractRow';
import CommodityOptionChainWidget from './components/CommodityOptionChainWidget';
import DashboardCommonCard from '../../components/DashboardCommonCard';
import ScrollIndicatorContainer from '../../components/ScrollIndicatorContainer';
import { ListRowSkeleton } from '../../components/DashboardSkeletons';
import { searchInstrument } from '../../../../services/backendAPI/index';
import { fetchMarketData } from '../../../../services/backendAPI/marketDataService';
import { findAvailableExpiries } from './utils/commodityUtils';
import { formatSmartExpiry } from '../../utils/expiryUtils';
const WATCHLIST_COMMODITIES = [
  { key: 'CRUDEOIL',    label: 'Crude Oil',        searchQuery: 'CRUDEOIL',   symbolMatch: 'CRUDEOIL' },
  { key: 'NATURALGAS',  label: 'Natural Gas',      searchQuery: 'NATURALGAS', symbolMatch: 'NATURALGAS' },
  { key: 'GOLD',        label: 'Gold',             searchQuery: 'GOLD',       symbolMatch: 'GOLD' },
  { key: 'SILVER',      label: 'Silver',           searchQuery: 'SILVER',     symbolMatch: 'SILVER' },
  { key: 'CRUDEOILM',   label: 'Crude Oil Mini',   searchQuery: 'CRUDEOILM',  symbolMatch: 'CRUDEOILM' },
  { key: 'NATGASMINI',  label: 'Natural Gas Mini', searchQuery: 'NATGASMINI', symbolMatch: 'NATGASMINI' },
  { key: 'GOLDM',       label: 'Gold Mini',        searchQuery: 'GOLDM',      symbolMatch: 'GOLDM' },
  { key: 'SILVERM',     label: 'Silver Mini',      searchQuery: 'SILVERM',    symbolMatch: 'SILVERM' },
];

const CommoditiesTab = () => {
  const theme = useTheme();
  const [activeInstIndex, setActiveInstIndex] = useState(0);
  const [expiryOptions, setExpiryOptions] = useState([]);
  const [selectedExpiryIndex, setSelectedExpiryIndex] = useState(0);
  const [loadingExpiries, setLoadingExpiries] = useState(false);
  const [liveFutureData, setLiveFutureData] = useState({ ltp: 0, abs: 0, pct: 0 });
  const [optionsValidating, setOptionsValidating] = useState(false);
  const lastFetchedToken = useRef(null);

  const selectedCommodity = WATCHLIST_COMMODITIES[activeInstIndex];
  const activeFuture = expiryOptions[selectedExpiryIndex]?.f || null;

  useEffect(() => {
    let cancelled = false;
    setLoadingExpiries(true);
    setExpiryOptions([]);
    setSelectedExpiryIndex(0);
    setLiveFutureData({ ltp: 0, abs: 0, pct: 0 });
    setOptionsValidating(false);
    lastFetchedToken.current = null;

    searchInstrument(selectedCommodity.searchQuery)
      .then((res) => {
        if (cancelled) return;
        const results = Array.isArray(res) ? res : (res?.result || res?.data || []);
        const expiries = findAvailableExpiries(results, selectedCommodity.symbolMatch);
        setExpiryOptions(expiries);
        setOptionsValidating(expiries.length > 0);
        setLoadingExpiries(false);
      })
      .catch(() => {
        if (!cancelled) setLoadingExpiries(false);
      });

    return () => { cancelled = true; };
  }, [selectedCommodity.searchQuery, selectedCommodity.symbolMatch]);

  // Fetch initial market data for the active future contract to avoid waiting for WebSocket
  useEffect(() => {
    if (!activeFuture) return;
    if (lastFetchedToken.current === activeFuture.token) return;
    lastFetchedToken.current = activeFuture.token;

    let cancelled = false;

    fetchMarketData(activeFuture.exchange, activeFuture.token)
      .then((dataRes) => {
        if (cancelled) return;
        const data = dataRes?.result || dataRes?.data;
        if (data) {
          setLiveFutureData({
            ltp: data.last_trade_price || data.lastTradedPrice || 0,
            abs: data.absoluteChange || data.change || 0,
            pct: data.percentChange || data.pct || 0,
          });
        }
      })
      .catch((err) => {
        console.error("Failed to fetch market data for active future", err);
      });

    return () => { cancelled = true; };
  }, [activeFuture?.token, activeFuture?.exchange]);

  const handlePriceUpdate = (data) => {
    setLiveFutureData({ ltp: data.price, abs: data.change, pct: data.pct });
  };

  // Auto-skip: remove current month from dropdown when it has no options
  const handleEmptyOptionChain = useCallback(() => {
    setExpiryOptions(prev => {
      const updated = prev.filter((_, i) => i !== selectedExpiryIndex);
      if (updated.length === 0) {
        setOptionsValidating(false); // No more months to try
      }
      return updated;
    });
    setSelectedExpiryIndex(0);
  }, [selectedExpiryIndex]);

  // Options loaded successfully — stop validating, show the widget
  const handleOptionsReady = useCallback(() => {
    setOptionsValidating(false);
  }, []);

  // Skeleton block for the OPTIONS section (reused in multiple places)
  const optionsSkeleton = (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1.5 }}>
        <Typography variant="subtitle2" fontWeight={800} color="text.primary">OPTIONS</Typography>
        <Skeleton animation="wave" variant="rounded" width={160} height={32} />
      </Box>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} lg={6}>
          <ListRowSkeleton count={5} rowHeight={56} />
        </Grid>
        <Grid item xs={12} lg={6}>
          <ListRowSkeleton count={5} rowHeight={56} />
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <Box sx={{ px: { xs: 1, lg: 3 }, pt: { xs: 0.5, lg: 1 }, pb: { xs: 1, lg: 3 }, maxWidth: '100%', margin: '0 auto' }}>
      
      <Box sx={{ mb: 3 }}>
        <Stack direction="column" spacing={0.5} sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 900, whiteSpace: 'nowrap' }}>
            Top Commodities
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
            Nearest Future Expiry Price Today
          </Typography>
        </Stack>

        {/* Cards Container */}
        <ScrollIndicatorContainer>
          {WATCHLIST_COMMODITIES.map((commodity, index) => (
            <DashboardCommonCard 
              key={commodity.key} 
              symbol={commodity.searchQuery}
              useNearestFuture={true}
              layout="fno"
              active={activeInstIndex === index}
              onClick={() => setActiveInstIndex(index)}
            />
          ))}
        </ScrollIndicatorContainer>
      </Box>

      {loadingExpiries ? (
        <Box sx={{ mb: 2 }}>
           <Typography variant="subtitle2" fontWeight="700" mb={1}>Nearest {selectedCommodity.label} Futures</Typography>
           <Paper sx={{ p: 2, border: '1px solid #E0E0E0', borderRadius: 2, height: 68, display: 'flex', alignItems: 'center' }} elevation={0}>
             <Skeleton animation="wave" width="30%" height={24} />
           </Paper>
        </Box>
      ) : activeFuture ? (
        <FutureContractRow 
          title={`Nearest ${selectedCommodity.label} Futures`}
          symbol={activeFuture.trading_symbol}
          expiry={formatSmartExpiry(activeFuture.expiry)}
          token={activeFuture.token}
          wsCode={4} // MCX
          exchange={activeFuture.exchange}
          price_precision={activeFuture.price_precision}
          onPriceUpdate={handlePriceUpdate}
          instrument={activeFuture}
        />
      ) : (
        <Box sx={{ py: 4, textAlign: 'center', bgcolor: 'action.hover', borderRadius: 2 }}>
          <Typography variant="body2" color="text.disabled">No active contract found</Typography>
        </Box>
      )}

      <Divider sx={{ my: 3, opacity: 0.5 }} />

      {loadingExpiries ? (
        optionsSkeleton
      ) : activeFuture ? (
        <Box sx={{ mt: 1 }}>
          {/* Show skeleton while validating (auto-skipping empty months) */}
          {optionsValidating && optionsSkeleton}

          {/* Widget renders hidden during validation so hooks can detect empty months.
              Becomes visible once a valid month with options is found. */}
          <Box sx={{ display: optionsValidating ? 'none' : 'block' }}>
            <CommodityOptionChainWidget 
              selectedCommodity={selectedCommodity} 
              activeFuture={activeFuture} 
              liveSpotPrice={liveFutureData.ltp}
              onEmptyChain={handleEmptyOptionChain}
              onOptionsReady={handleOptionsReady}
            />
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

export default CommoditiesTab;
